/**
 * Zwei getrennte Bereiche in einer App (Inhaber 03.11.2026): das Kundenportal (Agentur, Creator, öffentliche Seiten) und der
 * interne Bereich des Teams unter /intern (Betreiber-Konsole). Jeder Bereich hat seine eigene Anmeldung: eigener
 * Supabase-Anschluss mit eigenem Speicherschlüssel (data/index.ts), eigenes Anmeldefenster (/intern/anmelden). Im selben Browser
 * sind beide Sitzungen parallel möglich; Abmelden im einen beendet das andere nicht.
 *
 * Welcher Anschluss gilt, steht beim Laden der Seite fest (Pfad bzw. Host). Ein Wechsel des Bereichs innerhalb der App lädt
 * die Seite neu (app/routes.tsx, BereichWaechter), damit nie eine Seite mit der Sitzung des anderen Bereichs arbeitet.
 * Die Rechte prüft weiter allein die Datenbank.
 */

export const INTERN_BASIS = "/intern";
/** Speicherschlüssel der internen Sitzung; das Kundenportal behält den Standard von supabase-js (sb-<projekt>-auth-token) */
export const INTERN_SPEICHER = "co-intern-auth";
export const INTERN_ANMELDEN = "/intern/anmelden";

/** intern.creatoroffice.de (und jeder andere Host, der mit „intern.“ beginnt): dort ist „/“ der interne Bereich */
export function istInternHost(hostname: string): boolean {
  return hostname.toLowerCase().startsWith("intern.");
}

/** Pfade des internen Bereichs; die alten /betrieb-Adressen (Lesezeichen, Mails) gehören dazu und leiten auf /intern um */
export function istInternPfad(pathname: string): boolean {
  return /^\/(intern|betrieb)(\/|$)/.test(pathname);
}

/** /betrieb, /betrieb/… → /intern, /intern/…; alles andere unverändert */
export function betriebZuIntern(pfad: string): string {
  return pfad.replace(/^\/betrieb(?=\/|\?|#|$)/, INTERN_BASIS);
}

/** Ziel auf dem internen Host für einen Pfad außerhalb von /intern */
export function internZielAufHost(pathname: string): string {
  if (pathname === "/anmelden") return INTERN_ANMELDEN;
  if (pathname === "/passwort") return "/intern/passwort";
  return INTERN_BASIS;
}

/** Gilt für diese Seite der interne Anschluss? Einmal beim Laden entschieden. */
export function internerBereich(loc: { hostname: string; pathname: string } | undefined = typeof location === "undefined" ? undefined : location): boolean {
  if (!loc) return false;
  return istInternHost(loc.hostname) || istInternPfad(loc.pathname);
}

/** Optionen für createClient: getrennte Sitzung für den internen Bereich, Kundenportal unverändert */
export function anmeldeOptionen(intern: boolean): { persistSession: true; autoRefreshToken: true; storageKey?: string } {
  return intern ? { persistSession: true, autoRefreshToken: true, storageKey: INTERN_SPEICHER } : { persistSession: true, autoRefreshToken: true };
}

/**
 * Reines Mitarbeiterkonto: im Team der Konsole, aber ohne Agentur-Mitgliedschaft und ohne Creator-Profil. Im Kundenportal
 * bekommt es den Hinweis auf den internen Bereich statt der Einrichtung „Wie heißt deine Agentur?“. Wer beides ist, nutzt das
 * Kundenportal normal (die Konsole nur über /intern).
 */
export function nurInternesKonto(a: { isPlatformStaff?: boolean; isPlatformAdmin?: boolean; memberships: readonly unknown[]; talentLinks: readonly unknown[] }): boolean {
  return (!!a.isPlatformStaff || !!a.isPlatformAdmin) && a.memberships.length === 0 && a.talentLinks.length === 0;
}

/** Nach der internen Anmeldung weiter: nur Pfade im internen Bereich, sonst der Start der Konsole */
export function internWeiter(weiter: string | null | undefined): string {
  const w = typeof weiter === "string" ? betriebZuIntern(weiter) : "";
  if (!w.startsWith("/") || w.startsWith("//") || w.includes("\\")) return INTERN_BASIS;
  if (!/^\/intern(\/|\?|#|$)/.test(w) || w.startsWith(INTERN_ANMELDEN)) return INTERN_BASIS;
  return w;
}

/** Die Einladung ins Team öffnet auch ein Konto, das (noch) nicht zum Team gehört */
export function istTeamEinladung(pfad: string): boolean {
  return /^\/intern\/einladung(\?|$)/.test(pfad);
}
